import React, { useState } from 'react';
import styled from '@emotion/styled';
import Card from '@mui/material/Card';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Icon, IconButton, Typography } from '@mui/material';
import BackButtonSvg from './svgs/back.svg';
import ForwardButtonSvg from './svgs/forward.svg';
import { TeamMembers } from '@/app/(site)/about/team/components/team/constant';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';

const HostCardFront = styled(Card)`
  height: 100%;
  width: 100%;
  position: absolute;
  backface-visibility: hidden;
  border-radius: 12px;
  transition: 0.5s;
  transform: rotateY(0deg);
`;
const HostCardBack = styled(HostCardFront)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-radius: 12px;
  align-items: center;
  transform: rotateY(-180deg);
`;

const HostCard = styled(Card)`
  width: 100%;
  height: 290px;
  margin: 0;
  position: relative;
  background-color: transparent;
  box-shadow: 0 3.55064px 3.55064px rgba(0, 0, 0, 0.1);
  transform-style: preserve-3d;
  transition: 0.5s;
  :hover {
    ${HostCardFront} {
      transform: rotateY(-180deg);
    }
    ${HostCardBack} {
      transform: rotateY(0deg);
    }
  }
  @media (max-width: 768px) {
    /* margin: 10px 0; */
    width: 100%;
    max-width: 334px;
    height: 343px;
  }
`;

const ArrowButton = styled(IconButton)`
  position: absolute;
  right: 11px;
  bottom: 11px;
  :hover {
    opacity: 0.5;
  }
`;
const BackButton = styled(IconButton)`
  position: absolute;
  left: 11px;
  bottom: 11px;
  :hover {
    opacity: 0.5;
  }
`;
const BGImage = styled.div`
  position: absolute;
  background-color: #bf9bb9;
  height: 50%;
  width: 100%;
  ::after {
    content: '';
    position: absolute;
    bottom: -30px;
    left: 0;
    height: 40%;
    width: 100%;
    background: #ffffff;
    border-top-left-radius: 50%;
    border-top-right-radius: 50%;
    user-select: none;
  }
`;
const HostImage = styled(Image)`
  border-radius: 60% 70% 70% 60%;
  width: 132px;
  height: 132px;
  position: relative;
  background: white;
  margin-top: 25px;
  border: 6px solid #ffffff;
  z-index: 1;
  object-fit: cover;
  @media (max-width: 768px) {
    width: 177px;
    height: 177px;
  }
`;
HostImage.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
  sizes: '150px',
};

const ProfilePic = styled(HostImage)`
  width: 45px;
  height: 45px;
  border: 3px solid #bf9bb9;
  margin: 0;
  @media (max-width: 768px) {
    width: 77px;
    height: 77px;
  }
`;

const LowerDiv = styled.div`
  height: 40%;
  background: #ffffff;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;
const HostName = styled(Typography)`
  margin-top: 12px;
  font-family: var(--font-work-sans), sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 139.3%;
  color: #000000;
`;
const UpperDiv = styled.div`
  width: 100%;
  height: 55%;
  display: flex;
  justify-content: center;
`;
const ProfileDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 54px;
  width: 100%;
  padding: 24px 14px 14px 14px;
  @media (max-width: 768px) {
    height: 113px;
  }
`;
const ProfileName = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 139.3%;
  margin-left: 40px;
  color: #000000;
`;
const ProfileDesc = styled.div`
  padding: 6px 20px 0 20px;
  margin: 0;
`;
const Bio = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 160%;
  color: #231727;
`;
const Pronouns = styled(Typography)`
  font-family: var(--font-work-sans);
  font-size: 16px;
  line-height: 159.3%;
  text-align: center;
  color: #231727;
`;
export type HostsCardProps = {
  member?: TeamMembers;
};

const HostsCard = ({ member }: HostsCardProps) => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const iconDimensions = isMobile ? 36 : 24;
  const [transformF, setTransformF] = useState<string>('rotateY(0deg)');
  const [transformB, setTransformB] = useState<string>('rotateY(0deg)');
  return (
    <HostCard>
      <HostCardFront style={isMobile ? { transform: transformF } : {}}>
        <BGImage />
        <UpperDiv>
          {member?.pro_pic && <HostImage src={member?.pro_pic} alt="Host Image" />}
        </UpperDiv>
        <LowerDiv>
          <HostName>{member?.name}</HostName>
          <Pronouns>{member?.pronoun}</Pronouns>
        </LowerDiv>
        <ArrowButton
          style={{ height: iconDimensions, width: iconDimensions }}
          onClick={() => {
            setTransformF('rotateY(-180deg)');
            setTransformB('rotateY(0deg)');
          }}
        >
          <Icon>
            <ForwardButtonSvg />
          </Icon>
        </ArrowButton>
      </HostCardFront>
      <HostCardBack style={isMobile ? { transform: transformB } : {}}>
        <ProfileDiv>
          {member?.pro_pic && <ProfilePic src={member?.pro_pic} alt="Profile Picture" />}
          <ProfileName>{member?.name}</ProfileName>
        </ProfileDiv>
        <ProfileDesc>
          <Bio>{member?.info}</Bio>
        </ProfileDesc>

        <BackButton
          style={{ height: iconDimensions, width: iconDimensions }}
          onClick={() => {
            setTransformF('rotateY(0deg)');
            setTransformB('rotateY(180deg)');
          }}
        >
          <Icon>
            <BackButtonSvg />
          </Icon>
        </BackButton>
      </HostCardBack>
    </HostCard>
  );
};

export default HostsCard;
